/**
 * ChatHistoryDrawer - Side drawer for viewing and managing chat threads
 * Slides in from the left with Claude-style UI
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { lighten } from 'polished';
import { colors, spacing, fontStyles, buildPageSpacing } from '../../styles';
import { ChatThread } from '../../types/chat';
import { groupThreadsByDate } from '../../utils/chatHistoryUtils';
import Button from '../Button';
import ChatHistoryItem from './ChatHistoryItem';

interface ChatHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  threads: ChatThread[];
  activeThreadId: string | null;
  onSelectThread: (id: string) => void;
  onDeleteThread: (id: string) => void;
  onNewChat: () => void;
}

const ChatHistoryDrawer: React.FC<ChatHistoryDrawerProps> = ({
  isOpen,
  onClose,
  threads,
  activeThreadId,
  onSelectThread,
  onDeleteThread,
  onNewChat,
}) => {
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.getAttribute('data-theme') === 'dark');
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
    };

    handleThemeChange();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to trigger animation
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
      return;
    }
    setIsAnimating(false);
    // Wait for animation to complete before unmounting
    const timer = setTimeout(() => {
      setShouldRender(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Prevent background scrolling when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSelectThread = (id: string) => {
    onSelectThread(id);
    onClose();
  };

  const handleNewChat = () => {
    onNewChat();
    onClose();
  };

  if (!shouldRender) return null;

  const groupedThreads = groupThreadsByDate(
    [...threads].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
  );

  return createPortal(
    <div css={overlayStyle(isAnimating)} onClick={onClose}>
      <div
        css={drawerStyle(isAnimating, isDarkMode)}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Chat history"
      >
        {/* Header */}
        <div css={headerStyle}>
          <h2 css={headerTitleStyle(isDarkMode)}>Chats</h2>
          <button css={closeButtonStyle(isDarkMode)} onClick={onClose} aria-label="Close drawer" type="button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        {/* New Chat Button */}
        <div css={newChatContainerStyle}>
          <Button onClick={handleNewChat} variant="primary" block>
            + New Chat
          </Button>
        </div>

        {/* Thread List */}
        <div css={threadListStyle}>
          {threads.length === 0 ? (
            <div css={emptyStateStyle(isDarkMode)}>
              <p>No conversations yet</p>
              <p css={emptyStateSubtextStyle}>Start a new chat to get investment guidance</p>
            </div>
          ) : (
            <>
              {groupedThreads.today.length > 0 && (
                <div css={sectionStyle}>
                  <h3 css={sectionTitleStyle(isDarkMode)}>Today</h3>
                  {groupedThreads.today.map((thread) => (
                    <ChatHistoryItem
                      key={thread.id}
                      thread={thread}
                      isActive={thread.id === activeThreadId}
                      onSelect={() => handleSelectThread(thread.id)}
                      onDelete={() => onDeleteThread(thread.id)}
                    />
                  ))}
                </div>
              )}

              {groupedThreads.yesterday.length > 0 && (
                <div css={sectionStyle}>
                  <h3 css={sectionTitleStyle(isDarkMode)}>Yesterday</h3>
                  {groupedThreads.yesterday.map((thread) => (
                    <ChatHistoryItem
                      key={thread.id}
                      thread={thread}
                      isActive={thread.id === activeThreadId}
                      onSelect={() => handleSelectThread(thread.id)}
                      onDelete={() => onDeleteThread(thread.id)}
                    />
                  ))}
                </div>
              )}

              {groupedThreads.lastWeek.length > 0 && (
                <div css={sectionStyle}>
                  <h3 css={sectionTitleStyle(isDarkMode)}>Last 7 Days</h3>
                  {groupedThreads.lastWeek.map((thread) => (
                    <ChatHistoryItem
                      key={thread.id}
                      thread={thread}
                      isActive={thread.id === activeThreadId}
                      onSelect={() => handleSelectThread(thread.id)}
                      onDelete={() => onDeleteThread(thread.id)}
                    />
                  ))}
                </div>
              )}

              {groupedThreads.older.length > 0 && (
                <div css={sectionStyle}>
                  <h3 css={sectionTitleStyle(isDarkMode)}>Older</h3>
                  {groupedThreads.older.map((thread) => (
                    <ChatHistoryItem
                      key={thread.id}
                      thread={thread}
                      isActive={thread.id === activeThreadId}
                      onSelect={() => handleSelectThread(thread.id)}
                      onDelete={() => onDeleteThread(thread.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const overlayStyle = (isAnimating: boolean) => css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, ${isAnimating ? 0.5 : 0});
  z-index: 1001;
  transition: background-color 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const drawerStyle = (isAnimating: boolean, isDarkMode: boolean) => css`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: min(85vw, 320px);
  background-color: ${isDarkMode ? lighten(0.05, colors.liquorice) : colors.sugar};
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  transform: translateX(${isAnimating ? '0' : '-100%'});
  transition: transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${buildPageSpacing(spacing.deci, 'TOP')} ${spacing.deci} ${spacing.deci};
  border-bottom: 1px solid ${colors.cloud};
  flex-shrink: 0;

  html[data-theme='dark'] & {
    border-bottom-color: ${colors.tungsten};
  }
`;

const headerTitleStyle = (isDarkMode: boolean) => css`
  ${fontStyles.shark}
  margin: 0;
  color: ${isDarkMode ? colors.sugar : colors.liquorice};
`;

const closeButtonStyle = (isDarkMode: boolean) => css`
  background: none;
  border: none;
  padding: ${spacing.centi};
  cursor: pointer;
  color: ${isDarkMode ? colors.cloud : colors.silver};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const newChatContainerStyle = css`
  padding: ${spacing.deci};
  flex-shrink: 0;
`;

const threadListStyle = css`
  flex: 1;
  overflow-y: auto;
  padding: 0 ${spacing.deci} ${spacing.deci};
  -webkit-overflow-scrolling: touch;
`;

const sectionStyle = css`
  margin-bottom: ${spacing.deci};
`;

const sectionTitleStyle = (isDarkMode: boolean) => css`
  ${fontStyles.flea}
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${isDarkMode ? colors.tungsten : colors.silver};
  margin: 0 0 ${spacing.centi} 0;
  padding: ${spacing.centi} 0;
`;

const emptyStateStyle = (isDarkMode: boolean) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing.hecto} ${spacing.deci};
  text-align: center;

  p {
    ${fontStyles.cat}
    color: ${isDarkMode ? colors.tungsten : colors.silver};
    margin: 0;
  }
`;

const emptyStateSubtextStyle = css`
  ${fontStyles.mouse}
  margin-top: ${spacing.centi} !important;
  opacity: 0.7;
`;

export default ChatHistoryDrawer;
